import { action, makeObservable, observable } from 'mobx';
import { Icon } from '../icon';
import { MaterialIcon } from '../icon/icons';
import { IMithrilStateComponent } from '../mithril.component';
import { addStyles } from '../theme';
import { m, type MChild, type MithrilVNode } from '../../mithril';
import { type AnchoredPlacement, type AnchorRect, computeAnchoredPlacement } from './anchored.popover.position';
import { CSS, PANEL_Z_INDEX, Style } from './anchored.popover.style';

addStyles(Style);

export interface AnchoredPopoverContent {
	title: () => MChild | MChild[];
	/** Body content, re-evaluated each render so it tracks observable data (live preview). */
	content: () => MChild;
}

/**
 * A floating panel anchored to a trigger element. Placement is computed synchronously at open time
 * (and on resize/scroll) — no measure-then-reposition pass, so it shows in one render and ports
 * cleanly to a React ref + layout effect later.
 */
export class AnchoredPopover extends IMithrilStateComponent {
	private readonly width: number;

	@observable private isOpen = false;
	@observable.ref private placement: AnchoredPlacement | null = null;

	private contentDef: AnchoredPopoverContent | null = null;
	private anchorEl: HTMLElement | null = null;
	private readonly onReposition = () => this.reposition();

	constructor(opts: { width?: number } = {}) {
		super();
		makeObservable(this);
		this.width = opts.width ?? 320;
	}

	@action.bound open(anchor: HTMLElement, content: AnchoredPopoverContent) {
		this.anchorEl = anchor;
		this.contentDef = content;
		this.placement = this.place(anchor);
		this.isOpen = true;
		this.bindReposition();
	}

	@action.bound close() {
		this.isOpen = false;
		this.placement = null;
		this.contentDef = null;
		this.anchorEl = null;
		this.unbindReposition();
	}

	get nodes(): MithrilVNode | MithrilVNode[] {
		const placement = this.placement;
		if (!this.isOpen || this.contentDef == null || placement == null) {
			return null;
		}

		const style: Record<string, string | number> = {
			position: 'fixed',
			left: `${placement.left}px`,
			width: `${this.width}px`,
			maxHeight: `${placement.maxHeight}px`,
			zIndex: PANEL_Z_INDEX,
		};
		if (placement.top != null) {
			style.top = `${placement.top}px`;
		}
		if (placement.bottom != null) {
			style.bottom = `${placement.bottom}px`;
		}

		return [
			m('div', { className: CSS.backdrop, onclick: this.close }),
			m('div', { className: CSS.panel, style }, [
				m('div', { className: CSS.header }, [
					m('span', { className: CSS.title }, this.contentDef.title()),
					m(
						'button',
						{ type: 'button', className: CSS.close, title: 'Close', onclick: this.close },
						Icon.view(MaterialIcon.CLOSE)
					),
				]),
				m('div', { className: CSS.body }, this.contentDef.content()),
			]),
		];
	}

	private place(anchor: HTMLElement): AnchoredPlacement {
		return computeAnchoredPlacement({
			anchor: toAnchorRect(anchor.getBoundingClientRect()),
			viewport: { width: window.innerWidth, height: window.innerHeight },
			width: this.width,
		});
	}

	@action.bound private reposition() {
		if (this.anchorEl == null) {
			return;
		}
		this.placement = this.place(this.anchorEl);
	}

	private bindReposition() {
		window.addEventListener('resize', this.onReposition);
		window.addEventListener('scroll', this.onReposition, true);
	}

	private unbindReposition() {
		window.removeEventListener('resize', this.onReposition);
		window.removeEventListener('scroll', this.onReposition, true);
	}

	override onRemove() {
		super.onRemove();
		this.unbindReposition();
	}
}

function toAnchorRect(rect: DOMRect): AnchorRect {
	return {
		top: rect.top,
		left: rect.left,
		bottom: rect.bottom,
		right: rect.right,
		width: rect.width,
		height: rect.height,
	};
}
